// frontend/app/lessonAdmin.js
//
// API client for the admin lesson library: search every seeded lesson, open
// one, save it, and hand it back to the seeder.
//
// axios with a relative path rather than the fetchWithAuth of lessonPlans.js.
// The admin routes authorise on the X-User-Email header that AuthContext puts
// on axios.defaults at login, so an admin call made any other way arrives
// without the identity `_require_admin` looks for. Every other /admin call in
// InternalAdminPanel.js is made this way; one convention per surface.

import axios from "axios";

import { normalizeListResponse, PAGE_SIZE } from "./components/admin/adminTable";

// The router is mounted at /api/admin/lesson-plans and axios.defaults.baseURL
// already carries /api, which is why every admin path here starts at /admin.
const ADMIN_LESSON_PLANS = "/admin/lesson-plans";

/**
 * The server validates a save field by field and answers 400 with a detail
 * naming what it rejected. That message is the whole point of validating on
 * the server, so it is pulled out intact rather than replaced by a generic
 * failure line — an admin who cannot see which field is wrong will guess.
 *
 * Same chain as saveEdit in InternalAdminPanel.js — detail, then message, then
 * the fallback — with one addition: a 422 answers with a list of field errors,
 * and that chain would render it as "[object Object]".
 */
export const readAdminLessonError = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  const message = err?.response?.data?.message;
  if (typeof message === "string" && message) return message;
  // FastAPI's own 422 shape: [{ loc: [...], msg: "..." }].
  if (Array.isArray(detail) && detail.length) {
    const lines = detail
      .map((item) => {
        const where = Array.isArray(item?.loc) ? item.loc.slice(1).join(" › ") : "";
        const what = item?.msg || "";
        return where && what ? `${where}: ${what}` : what || where;
      })
      .filter(Boolean);
    if (lines.length) return lines.join("\n");
  }
  if (detail) return JSON.stringify(detail);
  return err?.message || fallback;
};

/** Drop empty filters so `?q=&board=` never reaches the server as a filter. */
const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    search.append(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

/**
 * One page of the library, as `{ items, total }`. `total` is null when the
 * server did not say how many there are — which the pager must read as
 * "unknown", not as zero.
 */
export async function listAdminLessonPlans({
  q = "",
  board = "",
  status = "",
  limit = PAGE_SIZE,
  offset = 0,
} = {}) {
  const res = await axios.get(
    `${ADMIN_LESSON_PLANS}${query({ q, board, status, limit, offset })}`
  );
  return normalizeListResponse(res?.data);
}

/** The full document: plan fields plus concepts[] and steps[] with blocks[]. */
export async function getAdminLessonPlan(lessonPlanId) {
  const res = await axios.get(`${ADMIN_LESSON_PLANS}/${lessonPlanId}`);
  return res?.data || null;
}

/**
 * Saves and locks. The response is the saved document, which the caller shows
 * instead of its own draft: what the server stored is the only truthful answer
 * to "did that land", and it carries the new lock state.
 */
export async function saveAdminLessonPlan(lessonPlanId, document) {
  const res = await axios.put(`${ADMIN_LESSON_PLANS}/${lessonPlanId}`, document);
  return res?.data || null;
}

/** Clears the lock, putting the lesson back under the seeder's control. */
export async function unlockAdminLessonPlan(lessonPlanId) {
  const res = await axios.post(`${ADMIN_LESSON_PLANS}/${lessonPlanId}/unlock`, {});
  return res?.data || null;
}
